import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { companyInfo } from '../data/mock';

const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact Us - Get Expert Help | Trademark Frontline</title>
        <meta name="description" content="Contact Trademark Frontline for expert trademark registration assistance. Available 24/7 to protect your brand. Get started today!" />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-[#FF7F7F] font-semibold text-lg mb-4">GET IN TOUCH</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#4B0082] mb-6 leading-tight">
              We're Here to Help
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Have questions about trademark registration, copyright protection, or any of our services? Our expert team is ready to assist you. Reach out today and let's protect your brand together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information and Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#4B0082] mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-12">
                <Card className="border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <Phone className="h-6 w-6 text-[#FF7F7F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#4B0082] mb-2">Phone</h3>
                        <p className="text-gray-600">{companyInfo.phone}</p>
                        <p className="text-sm text-gray-500 mt-1">Monday - Friday, 9AM - 6PM EST</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <Mail className="h-6 w-6 text-[#FF7F7F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#4B0082] mb-2">Email</h3>
                        <p className="text-gray-600">{companyInfo.email}</p>
                        <p className="text-sm text-gray-500 mt-1">We respond within 24 hours</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <MapPin className="h-6 w-6 text-[#FF7F7F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#4B0082] mb-2">Address</h3>
                        <p className="text-gray-600">{companyInfo.address}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 border-[#FF7F7F]/20 hover:border-[#FF7F7F] transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <Clock className="h-6 w-6 text-[#FF7F7F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#4B0082] mb-2">Business Hours</h3>
                        <div className="text-gray-600 space-y-1">
                          <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                          <p>Saturday: 10:00 AM - 4:00 PM EST</p>
                          <p>Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Why Contact Us */}
              <div className="bg-gradient-to-br from-[#4B0082] to-[#3A0062] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Why Contact Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-[#FF7F7F] mr-2">✓</span>
                    <span>Free consultation to discuss your brand protection needs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FF7F7F] mr-2">✓</span>
                    <span>Expert advice on trademark registration and copyright protection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FF7F7F] mr-2">✓</span>
                    <span>Quick response times and personalized service</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FF7F7F] mr-2">✓</span>
                    <span>Transparent pricing with no hidden fees</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <ContactForm
                title="Send Us a Message"
                ctaText="Send Message"
                showMessage={true}
              />
              
              <div className="mt-8 p-6 bg-[#FF7F7F]/10 rounded-xl border border-[#FF7F7F]/20">
                <h3 className="font-semibold text-[#4B0082] mb-3">Special Offer</h3>
                <p className="text-gray-600 mb-2">
                  Get started with trademark registration for just <span className="font-bold text-[#FF7F7F]">$45</span> + USPTO fees.
                </p>
                <p className="text-sm text-gray-500">
                  Limited time offer for new clients. Don't miss this opportunity to protect your brand at an unbeatable price.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#4B0082] mb-4">
              Need Quick Answers?
            </h2>
            <p className="text-gray-600 text-lg">
              Check out our frequently asked questions or explore our services for more information.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl cursor-pointer">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Phone className="h-8 w-8 text-[#FF7F7F]" />
                </div>
                <h3 className="text-xl font-bold text-[#4B0082] mb-2">Call Us</h3>
                <p className="text-gray-600 mb-4">Speak directly with our trademark experts</p>
                <p className="text-[#FF7F7F] font-semibold">{companyInfo.phone}</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl cursor-pointer">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Mail className="h-8 w-8 text-[#FF7F7F]" />
                </div>
                <h3 className="text-xl font-bold text-[#4B0082] mb-2">Email Us</h3>
                <p className="text-gray-600 mb-4">Get detailed responses to your inquiries</p>
                <p className="text-[#FF7F7F] font-semibold">{companyInfo.email}</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl cursor-pointer">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Clock className="h-8 w-8 text-[#FF7F7F]" />
                </div>
                <h3 className="text-xl font-bold text-[#4B0082] mb-2">Live Chat</h3>
                <p className="text-gray-600 mb-4">Instant support from our team</p>
                <p className="text-[#FF7F7F] font-semibold">Available 24/7</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-0">
        <div className="w-full h-96 bg-gray-200 relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
            alt="Office Location"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#4B0082]/30 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md text-center">
              <h3 className="text-2xl font-bold text-[#4B0082] mb-2">Visit Our Office</h3>
              <p className="text-gray-600 mb-4">{companyInfo.address}</p>
              <p className="text-sm text-gray-500">Schedule an appointment for in-person consultation</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;